import { CopyIconButton } from "@aurem/ui";
import { Mail } from "lucide-react";
import {
  gql,
  InternalEmployeeInvitationOrderByColumn,
  InternalEmployeeInvitationsFilterField,
} from "types";
import { FieldType, InternalResourceConfig } from "../lib/internal-resources";

import { Link } from "react-router-dom";

const renderEmployee = (value: unknown, item: unknown) => {
  const id = String(value ?? "");
  const invitationItem = item as {
    employee?: {
      id: string;
      firstName?: string;
      lastName?: string;
    };
  };
  const firstName = invitationItem?.employee?.firstName || "";
  const lastName = invitationItem?.employee?.lastName || "";
  const employeeName = `${firstName} ${lastName}`.trim() || "-";

  if (!id || id === "null" || id === "undefined") return employeeName;

  return (
    <div className="inline-flex items-center gap-1 group">
      <span>{employeeName}</span>
      <CopyIconButton
        copyContent={id}
        size="xs"
        variant="ghost"
        className="opacity-0 group-hover:opacity-100"
      />
    </div>
  );
};

const renderCompanyFromEmployee = (_value: unknown, item: unknown) => {
  const invitationItem = item as {
    employee?: {
      companyId?: string;
      company?: { id: string; name?: string };
    };
  };
  const companyId = invitationItem?.employee?.companyId;
  const companyName = invitationItem?.employee?.company?.name || "Unknown";

  if (!companyId) return "-";

  return (
    <div className="inline-flex items-center gap-1 group">
      <Link
        to={`/companies/${companyId}/dashboard/overview`}
        className="text-sm text-primary-600 hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {companyName}
      </Link>
      <CopyIconButton
        copyContent={companyId}
        size="xs"
        variant="ghost"
        className="opacity-0 group-hover:opacity-100"
      />
    </div>
  );
};

const GET_EMPLOYEE_INVITATION_LIST = gql(/* GraphQL */ `
  query InternalEmployeeInvitationList(
    $pagination: Pagination
    $filters: InternalEmployeeInvitationsFiltersExpression
    $orderBy: InternalEmployeeInvitationOrderByInput
  ) {
    internalEmployeeInvitationList(
      pagination: $pagination
      filters: $filters
      orderBy: $orderBy
    ) {
      count
      list {
        id
        email
        createdAt
        employeeId
        employee {
          id
          firstName
          lastName
          companyId
          company {
            id
            name
          }
        }
        inviterUserId
        user: inviterUser {
          id
          profile {
            name
          }
        }
      }
    }
  }
`);

export const employeeInvitationResourceConfig: InternalResourceConfig = {
  resourceName: "employee-invitation",
  displayNameKey: "apps.admin.internalResources.employeeInvitation.title",
  basePath: "/internal/data/employee-invitation",

  listQuery: GET_EMPLOYEE_INVITATION_LIST,
  listQueryName: "internalEmployeeInvitationList",

  fields: [
    {
      key: "email",
      labelKey: "apps.admin.internalResources.employeeInvitation.columns.email",
      type: FieldType.Email,
      sortKey: InternalEmployeeInvitationOrderByColumn.Email,
      width: 250,
    },
    {
      key: "employeeId",
      labelKey:
        "apps.admin.internalResources.employeeInvitation.columns.employeeId",
      type: FieldType.String,
      format: renderEmployee,
      width: 180,
    },
    {
      key: "employee.companyId",
      labelKey:
        "apps.admin.internalResources.employeeInvitation.columns.companyId",
      type: FieldType.String,
      format: renderCompanyFromEmployee,
      width: { min: 150, grow: 1 },
    },
    {
      key: "inviterUserId",
      labelKey:
        "apps.admin.internalResources.employeeInvitation.columns.inviterUserId",
      type: FieldType.User,
      width: 180,
    },
    {
      key: "createdAt",
      labelKey:
        "apps.admin.internalResources.employeeInvitation.columns.createdAt",
      type: FieldType.DateTime,
      sortKey: InternalEmployeeInvitationOrderByColumn.CreatedAt,
      width: 160,
    },
  ],

  list: {
    defaultSort: {
      column: InternalEmployeeInvitationOrderByColumn.CreatedAt,
      direction: "desc",
    },
    searchFields: [InternalEmployeeInvitationsFilterField.Email],
  },

  navigation: {
    icon: <Mail size={16} />,
    order: 3,
  },
};
